import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import { papCloseAuditModal } from "../bowlers/bowlers.redux";
import Spinner from "../ui/Spinner";

const StyledModal = styled(Modal)`
  @media (min-width: 992px) {
    .modal-lg,
    .modal-xl {
      max-width: 560px;
    }
  }

  .modal-content {
    background-color: #ffffff;
    border-top: 4px solid ${(p) => p.theme.color.primary};
    border-radius: ${(p) => p.theme.radius.md};
    overflow: hidden;
  }

  .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.md};
  }

  .modal-body {
    min-height: 50vh;
    max-height: 70vh;
    overflow-y: auto;
    padding: ${(p) => p.theme.spacing.md};
  }

  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  color: ${(p) => p.theme.color.primary};
  font-family: ${(p) => p.theme.font.family};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  font-size: ${(p) => p.theme.font.sizeLg};
  line-height: 1.2;
`;

const InfoCard = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.md};
  text-align: center;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};
  margin-bottom: ${(p) => p.theme.spacing.md};
`;

const TableScroll = styled.div`
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.md};
  overflow: hidden;
`;

const RecordsTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};

  thead th {
    background: ${(p) => p.theme.color.surfaceAlt};
    color: ${(p) => p.theme.color.primary};
    font-weight: ${(p) => p.theme.font.weightSemibold};
    text-align: left;
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
    border-bottom: 1px solid ${(p) => p.theme.color.border};
  }

  tbody td {
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
    border-bottom: 1px solid ${(p) => p.theme.color.border};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:nth-child(even) {
    background: rgba(230, 234, 245, 0.4);
  }
`;

const DirectionCell = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  color: ${(p) => p.theme.color.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(p) => p.theme.spacing.lg} 0;
  color: ${(p) => p.theme.color.textMuted};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
`;

const SpinnerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  width: 100%;
`;

const directionGlyph = (value) => {
  if (value === "Up") return "▲";
  if (value === "Down") return "▼";
  return value || "";
};

const PAPAuditModal = (props) => {
  const {
    showAuditModal,
    gettingAuditRecords,
    papCloseAuditModal,
    papAuditRecords,
  } = props;
  const { t } = useTranslation();

  const [disableTransition, setDisableTransition] = useState(false);

  useEffect(() => {
    if (gettingAuditRecords) {
      const timer = setTimeout(() => setDisableTransition(true), 500);
      return () => clearTimeout(timer);
    }
  }, [gettingAuditRecords]);

  const closeAuditModal = () => {
    setDisableTransition(false);
    papCloseAuditModal();
  };

  const records = papAuditRecords
    ? [...papAuditRecords]
        .filter(
          (record) => !Object.values(record).every((value) => value === "")
        )
        .reverse()
    : [];

  return (
    <StyledModal
      show={showAuditModal}
      onHide={closeAuditModal}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <TitleBar>
          <span>{t("PAP History")}</span>
        </TitleBar>
      </Modal.Header>
      <Modal.Body>
        {gettingAuditRecords && (
          <SpinnerWrap>
            <Spinner />
          </SpinnerWrap>
        )}
        {!gettingAuditRecords && (
          <>
            <InfoCard>
              {records.length === 0
                ? t("No PAP changes recorded")
                : `${records.length} ${
                    records.length === 1
                      ? t("PAP change recorded")
                      : t("PAP changes recorded")
                  }`}
            </InfoCard>
            {records.length === 0 ? (
              <EmptyState>{t("No PAP changes recorded")}</EmptyState>
            ) : (
              <TableScroll>
                <RecordsTable>
                  <thead>
                    <tr>
                      <th>H</th>
                      <th>V</th>
                      <th>{t("PAP Direction")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record, idx) => (
                      <tr key={idx}>
                        <td>{record.pap_h}</td>
                        <td>{record.pap_v}</td>
                        <td>
                          <DirectionCell>
                            {directionGlyph(record.pap_ud)}
                          </DirectionCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </RecordsTable>
              </TableScroll>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={closeAuditModal}>
          {t("Close")}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default connect(
  (state) => ({
    gettingAuditRecords: state.bowlers.selectedCustomer.gettingAuditRecords,
    showAuditModal: state.bowlers.selectedCustomer.showAuditModal,
    currentPapAuditRecord: state.bowlers.selectedCustomer.currentPapAuditRecord,
    papAuditRecords: state.bowlers.selectedCustomer.papAuditRecords,
  }),
  { papCloseAuditModal }
)(PAPAuditModal);
